import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ShoppingCart, Star, Eye, Package, Filter, Grid, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import ProcurementService from '@/api/procurement.service';
import { canCreate, canEdit, canDelete, canView } from '@/lib/permissions';

interface CatalogItem {
  id: number;
  publicId: string;
  businessId: number;
  supplierId: number;
  supplierName: string;
  itemName: string;
  itemDescription?: string;
  category: string;
  sku: string;
  unitPrice: number;
  currency: string;
  minimumOrderQuantity: number;
  leadTimeDays: number;
  availability: 'in_stock' | 'limited' | 'out_of_stock' | 'discontinued';
  rating: number;
  reviewCount: number;
  imageUrl?: string;
  specifications?: Record<string, any>;
  isActive: boolean;
  dateCreated: string;
  dateUpdated: string;
}

const SupplierMall: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [availabilityFilter, setAvailabilityFilter] = useState<string>('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [cart, setCart] = useState<{[key: string]: number}>({});

  const businessId = parseInt(localStorage.getItem('selectedBusiness') ? JSON.parse(localStorage.getItem('selectedBusiness')!).id : '1');

  useEffect(() => {
    fetchCatalogItems();
  }, [businessId]);

  const fetchCatalogItems = async () => {
    try {
      setLoading(true);
      const response = await ProcurementService.getSupplierCatalog(businessId);
      
      if (response.data.result === 'success') {
        setCatalogItems(response.data?.catalogItems || []);
      } else {
        toast({
          title: "Error",
          description: response.data.message || "Failed to fetch supplier catalog",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching supplier catalog:', error);
      toast({
        title: "Error",
        description: "Failed to fetch supplier catalog",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (item: CatalogItem, quantity: number = 1) => {
    setCart(prev => ({
      ...prev,
      [item.publicId]: (prev[item.publicId] || 0) + quantity
    }));
    toast({
      title: "Added to Cart",
      description: `${item.itemName} added to your cart`,
    });
  };

  const removeFromCart = (itemId: string) => {
    setCart(prev => {
      const newCart = { ...prev };
      delete newCart[itemId];
      return newCart;
    });
  };

  const createRequisitionFromCart = async () => {
    if (Object.keys(cart).length === 0) {
      toast({
        title: "Empty Cart",
        description: "Please add items to your cart first",
        variant: "destructive",
      });
      return;
    }

    try {
      const cartItems = Object.entries(cart).map(([itemId, quantity]) => {
        const item = catalogItems.find(i => i.publicId === itemId);
        return {
          itemId,
          quantity,
          unitPrice: item?.unitPrice || 0,
          supplierId: item?.supplierId
        };
      });

      await ProcurementService.createRequisitionFromCart(businessId, { items: cartItems });
      toast({
        title: "Success",
        description: "Purchase requisition created from cart items",
      });
      setCart({});
      navigate('/procurement/requisitions');
    } catch (error) {
      console.error('Error creating requisition from cart:', error);
      toast({
        title: "Error",
        description: "Failed to create requisition from cart",
        variant: "destructive",
      });
    }
  };

  const getAvailabilityBadge = (availability: string) => {
    const availabilityConfig = {
      in_stock: { variant: 'default' as const, label: 'In Stock' },
      limited: { variant: 'secondary' as const, label: 'Limited Stock' },
      out_of_stock: { variant: 'destructive' as const, label: 'Out of Stock' },
      discontinued: { variant: 'outline' as const, label: 'Discontinued' },
    };
    
    const config = availabilityConfig[availability as keyof typeof availabilityConfig] || availabilityConfig.in_stock;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-3 w-3 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  const filteredItems = catalogItems.filter(item => {
    const matchesSearch = item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || item.category === categoryFilter;
    const matchesAvailability = !availabilityFilter || item.availability === availabilityFilter;
    return matchesSearch && matchesCategory && matchesAvailability && item.isActive;
  });

  const categories = Array.from(new Set(catalogItems.map(item => item.category)));
  const cartItemCount = Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  const cartTotal = Object.entries(cart).reduce((sum, [itemId, qty]) => {
    const item = catalogItems.find(i => i.publicId === itemId);
    return sum + (item ? item.unitPrice * qty : 0);
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Supplier Mall</h1>
          <p className="text-muted-foreground">
            Browse and order from supplier catalogs
          </p>
        </div>
        <div className="flex gap-2">
          {cartItemCount > 0 && (
            <Button onClick={createRequisitionFromCart} className="flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />
              Cart ({cartItemCount}) - {formatCurrency(cartTotal)}
            </Button>
          )}
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            onClick={() => setViewMode('grid')}
            size="sm"
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            onClick={() => setViewMode('list')}
            size="sm"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredItems.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Suppliers</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Set(filteredItems.map(item => item.supplierId)).size}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Categories</CardTitle>
            <Filter className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{categories.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cart Items</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{cartItemCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search catalog items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md"
            >
              <option value="">All Availability</option>
              <option value="in_stock">In Stock</option>
              <option value="limited">Limited Stock</option>
              <option value="out_of_stock">Out of Stock</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Catalog Items */}
      <Card>
        <CardHeader>
          <CardTitle>Catalog Items ({filteredItems.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredItems.length === 0 ? (
                <div className="col-span-full text-center py-8">
                  <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No catalog items found</p>
                </div>
              ) : (
                filteredItems.map((item) => (
                  <Card key={item.id} className="overflow-hidden">
                    <div className="aspect-square bg-gray-100 relative">
                      {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.itemName} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        {getAvailabilityBadge(item.availability)}
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <h3 className="font-medium text-sm line-clamp-2">{item.itemName}</h3>
                        <p className="text-xs text-muted-foreground">{item.supplierName}</p>
                        <div className="flex items-center gap-1">
                          {renderStars(item.rating)}
                          <span className="text-xs text-muted-foreground">({item.reviewCount})</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="font-medium">{formatCurrency(item.unitPrice, item.currency)}</div>
                          <Badge variant="outline" className="text-xs">MOQ: {item.minimumOrderQuantity}</Badge>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => addToCart(item)}
                            disabled={item.availability === 'out_of_stock' || item.availability === 'discontinued'}
                          >
                            <ShoppingCart className="h-3 w-3 mr-1" />
                            Add to Cart
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/procurement/supplier-mall/${item.publicId}`)}
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>MOQ</TableHead>
                    <TableHead>Lead Time</TableHead>
                    <TableHead>Availability</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-8">
                        <Package className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                        <p className="text-muted-foreground">No catalog items found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                              {item.imageUrl ? (
                                <img src={item.imageUrl} alt={item.itemName} className="w-full h-full object-cover rounded" />
                              ) : (
                                <Package className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium">{item.itemName}</div>
                              <div className="text-sm text-muted-foreground">SKU: {item.sku}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{item.supplierName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.category}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{formatCurrency(item.unitPrice, item.currency)}</div>
                        </TableCell>
                        <TableCell>{item.minimumOrderQuantity}</TableCell>
                        <TableCell>{item.leadTimeDays} days</TableCell>
                        <TableCell>{getAvailabilityBadge(item.availability)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {renderStars(item.rating)}
                            <span className="text-xs text-muted-foreground">({item.reviewCount})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              onClick={() => addToCart(item)}
                              disabled={item.availability === 'out_of_stock' || item.availability === 'discontinued'}
                            >
                              <ShoppingCart className="h-3 w-3 mr-1" />
                              Add to Cart
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/procurement/supplier-mall/${item.publicId}`)}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cart Summary */}
      {cartItemCount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Shopping Cart ({cartItemCount} items)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(cart).map(([itemId, quantity]) => {
                const item = catalogItems.find(i => i.publicId === itemId);
                if (!item) return null;
                
                return (
                  <div key={itemId} className="flex items-center justify-between p-3 border rounded">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center">
                        <Package className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <div className="font-medium">{item.itemName}</div>
                        <div className="text-sm text-muted-foreground">{item.supplierName}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm">
                        {quantity} × {formatCurrency(item.unitPrice, item.currency)} = {formatCurrency(item.unitPrice * quantity, item.currency)}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeFromCart(itemId)}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="font-medium">Total: {formatCurrency(cartTotal)}</div>
                <Button onClick={createRequisitionFromCart}>
                  Create Purchase Requisition
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SupplierMall; 